// frontend/src/components/layout/AppShell.js
//
// Hub360 application shell, modeled on joao-1wes/dashboard-static and the
// 1wescom/wes-style-guide brand system. Replaces the previous MUI-based
// dashboard layout: the sidebar, topbar, dropdowns and body chrome are
// plain HTML + CSS classes from `styles/dashboardShell.css` — MUI only
// survives inside the page content that <Outlet /> renders. See
// .cursor/rules/design.mdc and CLAUDE.md for the design decisions.
//
// Preserved from the old Dashboard shell:
//   - tenant-scoped routing via getTenantPath
//   - environment switcher (lives in the topbar user area)
//   - language switcher (same — reuses existing <LanguageSwitcher />)
//   - notifications dropdown with a placeholder count
//   - collapsible sidebar (state persisted in localStorage)
//   - ChatAssistant floating widget
//   - dark-mode toggle via ThemeContext

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useEntitlements } from '../../context/EntitlementsContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { ROLE, resolveCanonicalRoleName } from '../../constants/roles';
import { tenantNameToSlug } from '../../utils/tenantSlug';
import { logout } from '../../services/authService';
import { getDashboardStats } from '../../services/dashboardService';
import LanguageSwitcher from '../common/LanguageSwitcher';
import ThemeToggle from '../common/ThemeToggle';
import { ChatAssistant } from '../insights';

import '../../styles/dashboardShell.css';
import '../../styles/appShell.css';
import '../../styles/appToast.css';
import '../../styles/designerComponents.css';
import '../../styles/modalOverrides.css';
import '../../styles/filterOverrides.css';
import '../../styles/integrations.css';
import '../../styles/campaigns.css';
import '../../styles/idpPage.css';

const SIDEBAR_COLLAPSED_KEY = 'hub360:sidebar-collapsed';

// Tiny helper — the sidebar and dropdowns both rely on "click outside to
// close" behavior. React portals aren't needed; a document listener
// guarded by the element's ref is enough.
function useClickAway(handler) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (event) => {
      if (!ref.current) return;
      if (ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [handler]);
  return ref;
}

// Material Symbols Rounded is already loaded by public/index.html.
function Icon({ name, className = '' }) {
  return <span className={`material-symbols-rounded ${className}`.trim()}>{name}</span>;
}

export default function AppShell() {
  const { t } = useTranslation();
  const { authenticated, user } = useAuth();
  const { isProductEnabled } = useEntitlements();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    environments,
    currentEnvironment,
    switchEnvironment,
    getCurrentEnvironmentName,
    canManageEnvironments,
  } = useEnvironment();

  // ---- state -------------------------------------------------------------
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState({});
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [envMenuOpen, setEnvMenuOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(3);
  const [counts, setCounts] = useState({});
  const displayName = user?.name || user?.full_name || user?.email || t('navigation.user', 'Usuário');
  const canonicalRoles = useMemo(
    () => [
      ...(user?.roles || []),
      ...(user?.platform_roles || []),
    ].map(resolveCanonicalRoleName),
    [user]
  );

  // ---- role helpers -------------------------------------------------------
  const isMenuAdmWes = useMemo(() => {
    return canonicalRoles.includes(ROLE.ADMIN_WES) || Boolean(user?.is_superuser);
  }, [canonicalRoles, user]);

  const isMenuAdmCliente = useMemo(() => {
    return canonicalRoles.includes(ROLE.ADMIN_CLIENTE);
  }, [canonicalRoles]);

  const isMenuSectorAdmin = useMemo(() => {
    return (
      canonicalRoles.some((r) => r === ROLE.ADMIN_WES || r === ROLE.ADMIN_CLIENTE) ||
      Boolean(user?.is_superuser)
    );
  }, [canonicalRoles, user]);

  const allowedSectionKeys = useMemo(() => {
    const rawAllowed = user?.allowed_section_keys || user?.allowedSections || null;
    if (!Array.isArray(rawAllowed) || rawAllowed.length === 0) return null;
    return new Set(rawAllowed.map(String));
  }, [user]);

  const canAccessSection = useCallback((sectionKey) => {
    if (!sectionKey || !allowedSectionKeys) return true;
    return allowedSectionKeys.has(sectionKey);
  }, [allowedSectionKeys]);

  // Apply `body.dashboard-shell` while the shell is mounted so the scoped
  // CSS in dashboardShell.css actually applies. Undone on unmount.
  useEffect(() => {
    document.body.classList.add('dashboard-shell');
    return () => document.body.classList.remove('dashboard-shell');
  }, []);

  // Persist collapsed state across sessions.
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  // Poll dashboard counts every 30s (mirror of the previous shell behavior).
  // Guards on auth to avoid 401 noise. Re-pull when tenant changes so the
  // sidebar counts match the active JWT (select-tenant, Fast Refresh, etc.).
  const tenantScopeKey =
    user?.tenant_id != null && user.tenant_id !== ''
      ? String(user.tenant_id)
      : user?.tenant_name || '';

  useEffect(() => {
    if (!authenticated || !tenantScopeKey) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const stats = await getDashboardStats();
        if (cancelled) return;
        setCounts({
          automations: stats.jobs?.total || 0,
          schedules: stats.schedules?.total || 0,
          queues: 0,
          executors: stats.agents?.total || 0,
          packages: stats.packages?.total || 0,
        });
      } catch {
        // 401s on startup are expected; ignore and let ProtectedRoute handle
      }
    };
    pull();
    const interval = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [authenticated, tenantScopeKey]);

  // ---- nav model ---------------------------------------------------------
  const menuGroups = useMemo(() => {
    // Hide any group/item whose product entitlement is explicitly disabled
    // (task #3782). Fail-open: an unset entitlement stays visible.
    const isEnabled = (entitlement) => !entitlement || isProductEnabled(entitlement);

    const baseMenu = [
      {
        id: 'overview',
        sectionKey: 'dashboard',
        icon: 'grid_view',
        label: t('navigation.dashboard'),
        path: '/dashboard',
      },
      {
        id: 'agents',
        sectionKey: 'dashboard',
        icon: 'smart_toy',
        label: t('navigation.agents'),
        path: '/dashboard/ai-agents',
      },
      {
        id: 'automation',
        sectionKey: 'automation',
        icon: 'device_hub',
        label: t('navigation.workflows'),
        items: [
          { label: t('navigation.schedules'), path: '/dashboard/schedules', count: counts.schedules },
          { label: t('navigation.inputFiles'), path: '/dashboard/input-files' },
          { label: t('navigation.automations'), path: '/dashboard/automations', count: counts.automations },
          { label: t('navigation.agentSkills'), path: '/dashboard/agent-skills' },
          { label: t('navigation.credentials'), path: '/dashboard/credentials' },
          { label: t('navigation.bpmn'), path: '/dashboard/bpmn' },
          {
            label: t('navigation.idp'),
            path: '/dashboard/automation/idp',
            entitlement: 'document_analysis',
          },
        ],
      },
      {
        id: 'infrastructure',
        sectionKey: 'infrastructure',
        icon: 'computer',
        label: t('navigation.infrastructure'),
        items: [
          { label: t('navigation.executors'), path: '/dashboard/executors', count: counts.executors },
          { label: t('navigation.packages'), path: '/dashboard/packages', count: counts.packages },
        ],
      },
      {
        id: 'dynamic-service',
        sectionKey: 'integrations',
        icon: 'account_tree',
        label: t('navigation.dynamicService'),
        labelClassName: 'nav-label--two-lines',
        items: [
          { label: t('navigation.campaigns'), path: '/dashboard/campaigns' },
          { label: t('navigation.integrationsChannels'), path: '/dashboard/integrations' },
          { label: t('navigation.hybridFlows'), path: '/dashboard/automation/hybrid-flows' },
          {
            label: t('navigation.voiceMessaging'),
            path: '/dashboard/voice-messaging',
            entitlement: 'voice_agent',
          },
        ],
      },
    ];

    const adminItems = [
      { label: t('navigation.auditLogs'), path: '/dashboard/audit' },
      { label: t('navigation.mcps'), path: '/dashboard/mcps' },
      { label: t('navigation.companies'), path: '/dashboard/companies' },
      { label: t('navigation.skills'), path: '/dashboard/skills' },
      ...(isMenuSectorAdmin ? [{ label: t('navigation.sectors'), path: '/dashboard/sectors' }] : []),
      { label: t('navigation.users'), path: '/dashboard/users' },
      ...(isMenuAdmWes
        ? [
            {
              label: t('navigation.organizations'),
              path: '/dashboard/organizations',
              entitlement: 'organizations_admin',
            },
          ]
        : []),
      {
        label: t('navigation.aiAudit'),
        path: '/dashboard/ai-audit',
        entitlement: 'audit_logs',
      },
    ];

    const rawGroups = [
      ...baseMenu,
      ...((isMenuAdmWes || isMenuAdmCliente)
        ? [{
            id: 'admin',
            sectionKey: 'administration',
            icon: 'admin_panel_settings',
            label: t('navigation.administration'),
            items: adminItems,
          }]
        : []),
    ];

    return rawGroups
      .filter((group) => canAccessSection(group.sectionKey))
      .filter((group) => isEnabled(group.entitlement))
      .map((group) =>
        group.items
          ? { ...group, items: group.items.filter((item) => isEnabled(item.entitlement)) }
          : group,
      )
      .filter((group) => group.path || (group.items && group.items.length > 0));
  }, [t, counts, isMenuAdmCliente, isMenuSectorAdmin, isMenuAdmWes, canAccessSection, isProductEnabled]);


  // ---- routing helpers --------------------------------------------------
  const getTenantPath = useCallback((path) => {
    if (!user || !user.tenant_name) return path;
    const slug = tenantNameToSlug(user.tenant_name);
    const clean = path.replace('/dashboard/', '').replace('/dashboard', '');
    return `/${slug}/${clean}`;
  }, [user]);

  const isActiveRoute = useCallback((path) => {
    if (!path) return false;
    const tenantAware = getTenantPath(path);
    if (location.pathname === tenantAware) return true;
    // "/" path is only active at exactly the root to avoid always-on highlight
    if (tenantAware !== '/' && location.pathname.startsWith(tenantAware + '/')) return true;
    return false;
  }, [location.pathname, getTenantPath]);

  const isActiveGroup = useCallback((group) => {
    if (!group.items) return false;
    return group.items.some((item) => isActiveRoute(item.path));
  }, [isActiveRoute]);

  const activeGroupId = useMemo(() => {
    const activeGroup = menuGroups.find((group) => (
      group.items && group.items.some((item) => isActiveRoute(item.path))
    ));
    return activeGroup?.id || null;
  }, [menuGroups, isActiveRoute]);

  useEffect(() => {
    if (collapsed) return;
    setExpandedMenu((prev) => {
      if (!activeGroupId) {
        return Object.keys(prev).length === 0 ? prev : {};
      }
      if (prev[activeGroupId] && Object.keys(prev).length === 1) {
        return prev;
      }
      return { [activeGroupId]: true };
    });
  }, [activeGroupId, collapsed]);

  const goTo = useCallback((path) => {
    navigate(getTenantPath(path));
    setMobileOpen(false);
    setNotificationsOpen(false);
    setUserMenuOpen(false);
    setEnvMenuOpen(false);
  }, [navigate, getTenantPath]);

  const toggleGroup = useCallback((groupId) => {
    if (collapsed) {
      // When collapsed, first click expands the sidebar AND opens the group.
      setCollapsed(false);
      setExpandedMenu({ [groupId]: true });
      return;
    }
    // Accordion behavior: opening a group collapses any other open one; a
    // second click on the same group closes it.
    setExpandedMenu((prev) => (prev[groupId] ? {} : { [groupId]: true }));
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (next) setExpandedMenu({});
      return next;
    });
  }, []);

  // ---- topbar title mapping ---------------------------------------------
  // When the user is on a leaf page (e.g. /dashboard/automations), show the
  // parent group label ("Automação") so the topbar always reflects where
  // the sidebar highlight sits, not the specific submenu item. Top-level
  // entries (like /dashboard itself) keep their own label.
  const pageTitle = useMemo(() => {
    for (const group of menuGroups) {
      if (group.path && isActiveRoute(group.path)) return group.label;
      if (group.items && group.items.some((item) => isActiveRoute(item.path))) {
        return group.label;
      }
    }
    return t('navigation.dashboard');
  }, [menuGroups, isActiveRoute, t]);

  // ---- dropdown refs (click-away) ---------------------------------------
  const notifRef = useClickAway(() => setNotificationsOpen(false));
  const userRef = useClickAway(() => setUserMenuOpen(false));
  const envRef = useClickAway(() => setEnvMenuOpen(false));

  // ---- handlers ---------------------------------------------------------
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchEnv = async (envId) => {
    try {
      await switchEnvironment(envId);
      setEnvMenuOpen(false);
    } catch (err) {
      console.error('Failed to switch environment:', err);
    }
  };

  const handleOpenNotifications = () => {
    setNotificationsOpen((v) => !v);
    setUserMenuOpen(false);
    setEnvMenuOpen(false);
    // Opening the panel resets the unread pill, like the previous shell did.
    if (!notificationsOpen) setNotificationCount(0);
  };

  // ---- render ------------------------------------------------------------
  return (
    <div className={`app-shell${collapsed ? ' sidebar-collapsed' : ''}${mobileOpen ? ' sidebar-mobile-open' : ''}`} id="appShell">
      <aside className="sidebar" aria-label={t('navigation.mainNavigation')} data-cy="app-sidebar">
        <div className="sidebar-top">
          <button
            type="button"
            className="icon-btn menu-btn"
            onClick={toggleCollapsed}
            aria-label={collapsed ? t('navigation.expandSidebar', 'Expandir menu') : t('navigation.collapseSidebar', 'Recolher menu')}
            aria-expanded={!collapsed}
          >
            <Icon name="menu" />
          </button>
        </div>

        <div className="logo">
          <img
            className="logo-big"
            src={`${process.env.PUBLIC_URL}/images/logo_big.svg`}
            alt="WES Hub 360"
          />
          <img
            className="logo-small"
            src={`${process.env.PUBLIC_URL}/images/logo_small.svg`}
            alt=""
          />
        </div>

        <nav className="nav" id="mainNavigation">
          {menuGroups.map((group) => (
            group.items ? (
              <div
                key={group.id}
                className={`nav-group${expandedMenu[group.id] ? ' open' : ''}${isActiveGroup(group) ? ' active' : ''}`}
              >
                <button
                  type="button"
                  className={`nav-item nav-trigger${isActiveGroup(group) ? ' active' : ''}${expandedMenu[group.id] ? ' open' : ''}`}
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={!!expandedMenu[group.id]}
                  aria-controls={`submenu-${group.id}`}
                  title={collapsed ? group.label : undefined}
                  data-cy={`nav-group-${group.id}`}
                >
                  <Icon name={group.icon} />
                  <span className={`nav-label${group.labelClassName ? ` ${group.labelClassName}` : ''}`}>{group.label}</span>
                  <span className="nav-caret" aria-hidden>▾</span>
                </button>
                <div
                  className={`submenu${expandedMenu[group.id] ? ' open' : ''}`}
                  id={`submenu-${group.id}`}
                >
                  {group.items.map((item) => (
                    <button
                      key={item.path}
                      type="button"
                      className={`submenu-item${isActiveRoute(item.path) ? ' active' : ''}`}
                      onClick={() => goTo(item.path)}
                      data-cy={`nav-item-${item.path.split('/').filter(Boolean).pop()}`}
                    >
                      <span className="submenu-label">{item.label}</span>
                      {item.count ? <span className="submenu-count">{item.count}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                key={group.id}
                type="button"
                className={`nav-item${isActiveRoute(group.path) ? ' active' : ''}`}
                onClick={() => goTo(group.path)}
                title={collapsed ? group.label : undefined}
                data-cy={`nav-item-${group.id}`}
              >
                <Icon name={group.icon} />
                <span className="nav-label">{group.label}</span>
              </button>
            )
          ))}

          <div className="nav-divider" />
        </nav>

      </aside>

      {/* Mobile backdrop — tapping it closes the drawer */}
      <div
        className="sidebar-backdrop"
        role="presentation"
        onClick={() => setMobileOpen(false)}
        hidden={!mobileOpen}
      />

      <main className="main">
        <header className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="icon-btn topbar-mobile-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t('navigation.openMenu', 'Abrir menu')}
            >
              <Icon name="menu" />
            </button>
            <h1 className="topbar-title">{pageTitle}</h1>
          </div>

          <div className="topbar-actions">
            <LanguageSwitcher variant="button" />
            <ThemeToggle variant="topbar" />

            {environments && environments.length > 1 && (
              <div className="environment-switcher" ref={envRef}>
                <button
                  type="button"
                  className="icon-btn environment-btn"
                  onClick={() => { setEnvMenuOpen((v) => !v); setNotificationsOpen(false); setUserMenuOpen(false); }}
                  aria-haspopup="menu"
                  aria-expanded={envMenuOpen}
                  title={getCurrentEnvironmentName?.() || ''}
                >
                  <Icon name="workspaces" />
                  <span className="environment-name">{getCurrentEnvironmentName?.() || t('environment.select', 'Ambiente')}</span>
                </button>
                <div className={`dropdown environment-dropdown${envMenuOpen ? ' open' : ''}`} role="menu">
                  <div className="dropdown-header">
                    {t('environment.switchTitle', 'Alternar ambiente')}
                  </div>
                  <ul className="dropdown-list">
                    {environments.map((env) => (
                      <li key={env.id}>
                        <button
                          type="button"
                          role="menuitem"
                          className={`dropdown-item${currentEnvironment?.id === env.id ? ' active' : ''}`}
                          onClick={() => handleSwitchEnv(env.id)}
                        >
                          <span>{env.name}</span>
                          {currentEnvironment?.id === env.id && <Icon name="check" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {canManageEnvironments && (
                    <div className="dropdown-footer">
                      <button type="button" className="dropdown-item" onClick={() => goTo('/dashboard/environments')}>
                        <Icon name="settings" />
                        <span>{t('environment.manage', 'Gerenciar ambientes')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="notifications" ref={notifRef}>
              <button
                type="button"
                className="icon-btn notifications-btn"
                onClick={handleOpenNotifications}
                aria-haspopup="dialog"
                aria-expanded={notificationsOpen}
                aria-label={t('navigation.notifications')}
              >
                <Icon name="notifications" />
                {notificationCount > 0 && <span className="notifications-badge">{notificationCount}</span>}
              </button>
              <div className={`dropdown notifications-dropdown${notificationsOpen ? ' open' : ''}`} role="dialog">
                <div className="notifications-header">
                  <span>{t('navigation.notifications')}</span>
                  <button type="button" className="link-btn" onClick={() => setNotificationCount(0)}>
                    {t('notifications.markAllAsRead')}
                  </button>
                </div>
                <ul className="notifications-list">
                  <li>
                    <p className="notification-title">{t('notifications.jobCompleted')}</p>
                    <p className="notification-meta">{t('notifications.dailyReport')}</p>
                  </li>
                  <li>
                    <p className="notification-title">{t('notifications.newExecutor')}</p>
                    <p className="notification-meta">{t('notifications.executorOnline')}</p>
                  </li>
                  <li>
                    <p className="notification-title">{t('notifications.packageUploaded')}</p>
                  </li>
                </ul>
              </div>
            </div>

            <div className="user-menu" ref={userRef}>
              <button
                type="button"
                className="user-menu-trigger"
                onClick={() => { setUserMenuOpen((v) => !v); setNotificationsOpen(false); setEnvMenuOpen(false); }}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                data-cy="user-menu-trigger"
              >
                <span className="user-avatar" aria-hidden>
                  {displayName.slice(0, 1).toUpperCase()}
                </span>
                <span className="user-meta">
                  <span className="user-name">{displayName}</span>
                  {user?.tenant_name && <span className="user-tenant">{user.tenant_name}</span>}
                </span>
                <Icon name="expand_more" className="user-caret" />
              </button>
              <div className={`dropdown user-dropdown${userMenuOpen ? ' open' : ''}`} role="menu">
                <button type="button" role="menuitem" className="dropdown-item" onClick={() => goTo('/dashboard/profile')} data-cy="user-menu-profile">
                  <Icon name="person" />
                  <span>{t('navigation.profile')}</span>
                </button>
                {isMenuAdmWes && (
                  <button
                    type="button"
                    role="menuitem"
                    className="dropdown-item"
                    onClick={() => {
                      navigate('/select-tenant');
                      setUserMenuOpen(false);
                      setNotificationsOpen(false);
                      setEnvMenuOpen(false);
                      setMobileOpen(false);
                    }}
                    data-cy="switch-company-btn"
                  >
                    <Icon name="business" />
                    <span>{t('navigation.switchCompany', 'Trocar empresa')}</span>
                  </button>
                )}
                <button type="button" role="menuitem" className="dropdown-item" onClick={() => goTo('/dashboard/settings')} data-cy="user-menu-settings">
                  <Icon name="settings" />
                  <span>{t('navigation.settings')}</span>
                </button>
                <div className="dropdown-divider" />
                <button type="button" role="menuitem" className="dropdown-item danger" onClick={handleLogout} data-cy="user-menu-logout">
                  <Icon name="logout" />
                  <span>{t('navigation.logout')}</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <section className="content">
          <Outlet />
        </section>
      </main>

      {/* floating=true renders the FAB + toggleable drawer. Default
       * floating=false keeps the panel permanently open. */}
      <ChatAssistant floating />
    </div>
  );
}
